import { v4 as uuidv4 } from 'uuid'
import { safeGetLocalStorage, safeSetLocalStorage } from './safe-local-storage'

/**
 * Storage key (and cookie name) for the device ID.
 */
const DEVICE_ID_KEY = 'taka_deviceId'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 // 1 year

/**
 * In-memory cache — the only storage guaranteed to work. It keeps the device ID
 * stable for the lifetime of the page session even when both localStorage and
 * cookies are blocked (Safari Private, Instagram/Facebook in-app webviews,
 * partitioned storage). Without it, `getDeviceId()` minted a fresh UUID on every
 * call in those browsers, so the OTP-request deviceId no longer matched the
 * OTP-verify deviceId and the correct code was rejected with 401.
 */
let cached: string | null = null

const readCookie = (): string | null => {
  try {
    const row = document.cookie.split('; ').find(r => r.startsWith(`${DEVICE_ID_KEY}=`))
    return row ? decodeURIComponent(row.slice(DEVICE_ID_KEY.length + 1)) : null
  } catch {
    return null
  }
}

const writeCookie = (id: string): void => {
  try {
    const secure = location.protocol === 'https:' ? '; Secure' : ''
    // Host-only (no Domain) so it never leaks to taka.ai subdomains.
    document.cookie = `${DEVICE_ID_KEY}=${encodeURIComponent(
      id
    )}; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`
  } catch {
    /* blocked — ignore */
  }
}

/**
 * Gets a stable device ID for this client, generating one if needed.
 *
 * The device ID is used for:
 * - OTP authentication security (binding OTP to a specific device)
 * - Session management
 * - Analytics and tracking user interactions across sessions
 *
 * Read ladder (existing id wins, so returning users are unaffected):
 *   in-memory cache → localStorage → cookie → mint a new UUID.
 * The resolved id is then written back everywhere best-effort. Every storage
 * access is wrapped, so a blocked store degrades silently and this never throws.
 *
 * @returns The device ID for this client
 */
export const getDeviceId = (): string => {
  if (cached) return cached

  let id = safeGetLocalStorage(DEVICE_ID_KEY) ?? readCookie()
  if (!id) id = uuidv4()

  cached = id
  safeSetLocalStorage(DEVICE_ID_KEY, id)
  writeCookie(id)
  return id
}
